import React, { useRef } from 'react';
import { Box, ButtonBase, Button, Popover, Typography } from '@mui/material';
import { tokens } from '../../theme/tokens';
import { useShellTranslation } from '../navigation/useShellTranslation';
import { useShell } from '../navigation/ShellContext';
import { APP_UPDATE_ATTR, useAppUpdate } from './UpdateContext';

const { ink, accent, signal, surface, line, radius, shadow } = tokens;

/** The 7px status dot shared by the chip and the toast. */
const Dot: React.FC<{ color: string }> = ({ color }) => (
  <Box
    component="span"
    aria-hidden
    sx={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }}
  />
);

const popoverButtonSx = {
  height: 32,
  minWidth: 0,
  borderRadius: `${radius.control}px`,
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
} as const;

/**
 * Header chip for a pending update. It stays until the page reloads (there is
 * no dismiss), and clicking it opens `UpdatePopover`. Hidden for `required`,
 * where the dialog takes over.
 */
export const UpdateChip: React.FC = () => {
  const { t } = useShellTranslation();
  const { closeOverlay } = useShell();
  const { urgency, reason, toastVisible, popoverOpen, setPopoverOpen, snooze, refresh } =
    useAppUpdate();
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  const pending = urgency === 'normal' || urgency === 'soon';
  // While the first-notice toast is up it is the one prompt on screen.
  if (!pending || toastVisible) return null;

  const soon = urgency === 'soon';
  const tone = soon ? signal.caution : signal.info;
  const label = t(soon ? 'shell.update.chip.soon' : 'shell.update.chip.normal');

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={popoverOpen}
        onClick={() => {
          closeOverlay(); // one popover at a time
          setPopoverOpen(!popoverOpen);
        }}
        sx={{
          flexShrink: 0,
          height: 30,
          display: 'flex',
          alignItems: 'center',
          gap: '7px',
          p: { xs: '0 9px', sm: '0 11px 0 9px' },
          border: `1px solid ${tone.border}`,
          background: tone.tint,
          borderRadius: `${radius.pill}px`,
          fontSize: '12.5px',
          fontWeight: 600,
          color: tone.ink,
          whiteSpace: 'nowrap',
          '&:hover': { background: soon ? tokens.signal.caution.row : accent.tintStrong },
        }}
      >
        <Dot color={tone.solid} />
        {/* Below sm the dot alone stands in, so the header still fits at 320px. */}
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
          {label}
        </Box>
      </ButtonBase>

      <Popover
        open={popoverOpen}
        anchorEl={anchorRef.current}
        onClose={() => setPopoverOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          root: { [APP_UPDATE_ATTR]: '' } as Record<string, string>,
          paper: {
            role: 'dialog',
            'aria-labelledby': 'app-update-popover-title',
            sx: {
              mt: '6px',
              width: soon ? 280 : 300,
              maxWidth: 'calc(100vw - 32px)',
              p: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              background: surface.card,
              border: `1px solid ${line.default}`,
              borderRadius: `${radius.panel}px`,
              boxShadow: shadow.menu,
            },
          },
        }}
      >
        <Typography
          id="app-update-popover-title"
          sx={{ fontSize: '14.5px', fontWeight: 700, color: ink.primary }}
        >
          {t(soon ? 'shell.update.soon.title' : 'shell.update.normal.title')}
        </Typography>
        <Typography sx={{ fontSize: '13px', lineHeight: 1.5, color: ink.bodyAlt }}>
          {soon
            ? reason || t('shell.update.soon.fallbackReason')
            : t('shell.update.normal.body')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', pt: '4px' }}>
          <Button
            onClick={snooze}
            sx={{
              ...popoverButtonSx,
              px: '12px',
              color: ink.bodyAlt,
              '&:hover': { background: surface.tint },
            }}
          >
            {t(soon ? 'shell.update.snooze.soon' : 'shell.update.snooze.normal')}
          </Button>
          <Button
            variant="contained"
            onClick={refresh}
            sx={{
              ...popoverButtonSx,
              px: '14px',
              background: accent.main,
              color: ink.inverse,
              '&:hover': { background: accent.hover, boxShadow: 'none' },
            }}
          >
            {t('shell.update.refreshNow')}
          </Button>
        </Box>
      </Popover>
    </>
  );
};
